import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import Sidebar from './Sidebar';
import { FaBars, FaTimes, FaClock, FaCheck, FaTimes as FaX, FaVideo, FaFileAlt, FaDownload, FaRedo, FaShieldAlt, FaGift, FaLock, FaBook, FaClipboardList, FaCalendarAlt, FaGraduationCap, FaInfoCircle, FaUsers } from 'react-icons/fa';
import { getStudentBookingRequests, deleteBookingRequest, updateBookingRequestStatus, requestReschedule } from './bookingService';
import { supabase } from './supabaseClient';
import PageHeader from './components/PageHeader';
import { useNavigate } from 'react-router-dom';

export default function RequestsPage() {
  const {
    isArabic,
    sidebarVisible,
    setSidebarVisible,
    cachedUserProfile,
    userProfileLoading
  } = useOutletContext() || {};
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  // Handler for language change from Sidebar
  const handleLanguageChange = (arabic) => {
    // Language change is handled by the layout context
    console.log('RequestsPage: handleLanguageChange called with:', arabic);
  };
  const [requests, setRequests] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'upcoming', or 'past'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // request id
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [expandedSession, setExpandedSession] = useState(null); // For transcript/summary expansion
  const [selectedSession, setSelectedSession] = useState(null); // For showing recording and summary modal
  const [teacherProfiles, setTeacherProfiles] = useState({}); // Store teacher profile photos
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    newDate: '',
    newTimeFrom: '',
    newTimeTo: '',
    reason: ''
  });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // OPTIMIZATION: Get student email from cached profile (from layout) instead of localStorage
  const studentEmail = cachedUserProfile?.email || localStorage.getItem('userEmail') || '';
  const userRole = localStorage.getItem('userRole');

  console.log('[Orders] Using cached profile:', cachedUserProfile?.full_name, 'Email:', studentEmail);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await getStudentBookingRequests(studentEmail);
      if (error) {
        setError('Failed to fetch requests');
        return;
      }
      const pendingRequests = (data || []).filter(req => req.status === 'pending');
      setRequests(pendingRequests);
      
      // Fetch teacher profile photos
      const teacherEmails = [...new Set(pendingRequests.map(req => req.teacher_email))];
      if (teacherEmails.length > 0) {
        const { data: teachers } = await supabase
          .from('Teachers')
          .select('email, profile_photo')
          .in('email', teacherEmails);
        
        const profiles = {};
        (teachers || []).forEach(teacher => {
          profiles[teacher.email] = teacher.profile_photo;
        });
        setTeacherProfiles(prev => ({ ...prev, ...profiles }));
      }
    } catch (err) {
      setError('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  }, [studentEmail]);

  const fetchPastSessions = useCallback(async () => {
    try {
      setLoading(true);
      // Include own bookings OR group bookings where student is a participant
      const { data, error } = await supabase
        .from('booking_requests')
        .select('*')
        .or(`student_email.eq.${studentEmail},participant_emails.cs.{"${studentEmail}"}`)
        .eq('status', 'accepted')
        .lt('date', new Date().toISOString().split('T')[0]) // Past dates only
        .order('date', { ascending: false })
        .order('time_from', { ascending: false });

      if (error) {
        setError('Failed to fetch past sessions');
        return;
      }
      setPastSessions(data || []);

      // Fetch teacher profile photos for past sessions
      const teacherEmails = [...new Set((data || []).map(session => session.teacher_email))];
      if (teacherEmails.length > 0) {
        const { data: teachers } = await supabase
          .from('Teachers')
          .select('email, profile_photo')
          .in('email', teacherEmails);

        const profiles = {};
        (teachers || []).forEach(teacher => {
          profiles[teacher.email] = teacher.profile_photo;
        });
        setTeacherProfiles(prev => ({ ...prev, ...profiles }));
      }
    } catch (err) {
      setError('Failed to fetch past sessions');
    } finally {
      setLoading(false);
    }
  }, [studentEmail]);

  const fetchUpcomingSessions = useCallback(async () => {
    try {
      setLoading(true);
      // Include own bookings OR group bookings where student is a participant
      const { data, error } = await supabase
        .from('booking_requests')
        .select('*')
        .or(`student_email.eq.${studentEmail},participant_emails.cs.{"${studentEmail}"}`)
        .eq('status', 'accepted')
        .gte('date', new Date().toISOString().split('T')[0]) // Today and future dates
        .order('date', { ascending: true })
        .order('time_from', { ascending: true });

      if (error) {
        setError('Failed to fetch upcoming sessions');
        return;
      }
      setUpcomingSessions(data || []);

      // Fetch teacher profile photos for upcoming sessions
      const teacherEmails = [...new Set((data || []).map(session => session.teacher_email))];
      if (teacherEmails.length > 0) {
        const { data: teachers } = await supabase
          .from('Teachers')
          .select('email, profile_photo')
          .in('email', teacherEmails);

        const profiles = {};
        (teachers || []).forEach(teacher => {
          profiles[teacher.email] = teacher.profile_photo;
        });
        setTeacherProfiles(prev => ({ ...prev, ...profiles }));
      }
    } catch (err) {
      setError('Failed to fetch upcoming sessions');
    } finally {
      setLoading(false);
    }
  }, [studentEmail]);

  useEffect(() => {
    // Wait for user profile to load before fetching requests
    if (userProfileLoading || !studentEmail) {
      console.log('[Orders] Waiting for user profile...', { userProfileLoading, studentEmail });
      return;
    }

    console.log('[Orders] User profile loaded, fetching requests for tab:', activeTab);
    if (activeTab === 'pending') {
      fetchRequests();
    } else if (activeTab === 'upcoming') {
      fetchUpcomingSessions();
    } else {
      fetchPastSessions();
    }
  }, [activeTab, fetchRequests, fetchUpcomingSessions, fetchPastSessions, userProfileLoading, studentEmail]);

  const handleRemoveRequest = async (requestId) => {
    try {
      const { error } = await deleteBookingRequest(requestId);
      if (error) {
        setError('Failed to remove request');
        return;
      }
      // Remove from local state
      setRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (err) {
      setError('Failed to remove request');
    }
  };

  // Accept/Reject handler
  async function handleAction(reqId, status) {
    setTestMessage('Button clicked: ' + status);
    if (status === 'accepted') alert('Accept clicked');
    if (status === 'rejected') alert('Reject clicked');
    setActionLoading(reqId + '-' + status);
    setActionError('');
    setActionSuccess('');
    console.log('Calling updateBookingRequestStatus', reqId, status);
    try {
      const { data, error } = await updateBookingRequestStatus(reqId, status);
      console.log('updateBookingRequestStatus result:', { data, error });
      if (error) throw error;
      setActionSuccess('Request ' + status + 'ed successfully!');
      await fetchRequests(); // Refresh list after action
    } catch (err) {
      setActionError('Failed to update request: ' + (err.message || err));
      console.error('handleAction error:', err);
    } finally {
      setTimeout(() => setActionLoading(null), 500);
      setTimeout(() => setActionSuccess(''), 2000);
    }
  }

  // Check if booking is eligible for reschedule
  const isRescheduleEligible = (request) => {
    if (!request) return false;

    // Must be paid
    if (request.payment_status !== 'paid') return false;

    // Must be accepted
    if (request.status !== 'accepted') return false;

    // Can't have pending reschedule
    if (request.reschedule_status === 'pending') return false;

    // Max 1 reschedule
    if ((request.reschedule_count || 0) >= 1) return false;

    // Must be 24+ hours away
    const sessionDateTime = new Date(`${request.date} ${request.time_from}`);
    const now = new Date();
    const hoursUntilSession = (sessionDateTime - now) / (1000 * 60 * 60);

    return hoursUntilSession >= 24;
  };

  // Get ineligibility reason for tooltip
  const getRescheduleIneligibilityReason = (request) => {
    if (!request) return '';
    if (request.payment_status !== 'paid') return isArabic ? 'يتطلب الدفع' : 'Payment required';
    if (request.status !== 'accepted') return isArabic ? 'لم يتم القبول بعد' : 'Not accepted yet';
    if (request.reschedule_status === 'pending') return isArabic ? 'إعادة الجدولة قيد الانتظار' : 'Reschedule pending';
    if ((request.reschedule_count || 0) >= 1) return isArabic ? 'تمت إعادة الجدولة مسبقاً' : 'Already rescheduled once';

    const sessionDateTime = new Date(`${request.date} ${request.time_from}`);
    const now = new Date();
    const hoursUntilSession = (sessionDateTime - now) / (1000 * 60 * 60);

    if (hoursUntilSession < 24) {
      return isArabic
        ? `أقل من 24 ساعة (${Math.round(hoursUntilSession)} ساعة متبقية)`
        : `Less than 24 hours (${Math.round(hoursUntilSession)}h remaining)`;
    }

    return '';
  };

  // Convert 24-hour time to 12-hour format (e.g., "14:30" -> "2:30 PM")
  const convertTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours24, minutes] = time24.split(':');
    let hours = parseInt(hours24);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert 0 to 12 for midnight
    return `${hours}:${minutes} ${ampm}`;
  };

  // Convert 12-hour time to 24-hour format (e.g., "2:30 PM" -> "14:30")
  const convertTo24Hour = (time12) => {
    if (!time12) return '';
    const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return '';
    let [, hours, minutes, ampm] = match;
    hours = parseInt(hours);
    if (ampm.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  };

  const getStatusColor = (status, paymentStatus) => {
    // For accepted bookings, show payment status colors
    if (status === 'accepted') {
      if (paymentStatus === 'awaiting_payment') {
        return '#f59e0b'; // Orange for awaiting payment
      }
      if (paymentStatus === 'paid') {
        return '#10B981'; // Green for paid/confirmed
      }
      if (paymentStatus === 'payment_expired') {
        return '#dc2626'; // Red for expired
      }
    }

    switch (status) {
      case 'pending':
        return '#FFB400';
      case 'accepted':
        return '#10B981';
      case 'rejected':
      case 'declined':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getStatusIcon = (status, paymentStatus) => {
    // For accepted bookings, show payment status icons
    if (status === 'accepted') {
      if (paymentStatus === 'awaiting_payment') {
        return <FaClock size={16} />; // Clock for awaiting payment
      }
      if (paymentStatus === 'paid') {
        return <FaCheck size={16} />; // Check for paid/confirmed
      }
      if (paymentStatus === 'payment_expired') {
        return <FaX size={16} />; // X for expired
      }
    }

    switch (status) {
      case 'pending':
        return <FaClock size={16} />;
      case 'accepted':
        return <FaCheck size={16} />;
      case 'rejected':
      case 'declined':
        return <FaX size={16} />;
      default:
        return <FaClock size={16} />;
    }
  };

  const getStatusLabel = (status, paymentStatus, isArabic) => {
    // For accepted bookings, show payment status labels
    if (status === 'accepted') {
      if (paymentStatus === 'awaiting_payment') {
        return isArabic ? 'في انتظار الدفع' : 'Awaiting Payment';
      }
      if (paymentStatus === 'paid') {
        return isArabic ? 'مؤكد' : 'Confirmed';
      }
      if (paymentStatus === 'payment_expired') {
        return isArabic ? 'انتهت صلاحية الدفع' : 'Payment Expired';
      }
    }

    // Default status labels
    switch (status) {
      case 'pending':
        return isArabic ? 'قيد الانتظار' : 'Pending';
      case 'accepted':
        return isArabic ? 'مقبول' : 'Accepted';
      case 'rejected':
      case 'declined':
        return isArabic ? 'مرفوض' : 'Declined';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    // Parse date string manually to avoid timezone issues
    // Input format: "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS"
    const dateOnly = dateString.split('T')[0]; // Handle ISO datetime strings
    const [year, month, day] = dateOnly.split('-').map(Number);
    // Create date in local timezone (month is 0-indexed in Date constructor)
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    // Parse date string manually to avoid timezone issues
    const dateOnly = dateString.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return timeString; // Already in 12-hour format
  };

  // Show loading while profile or requests are loading
  if (userProfileLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 12 }}>
        <FaBook size={32} style={{ color: '#6B7280' }} />
        <div style={{ color: '#6B7280', fontWeight: 600 }}>
          {userProfileLoading ? (isArabic ? 'جاري تحميل الملف الشخصي...' : 'Loading profile...') : (isArabic ? 'جاري التحميل...' : 'Loading...')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          background: '#f7f9fb',
          fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
        }}
      >
        <Sidebar
          isOpen={sidebarOpen}
          isVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          userRole="student"
          dashboardRoute="/dashboard/student"
          isArabic={isArabic}
          onLanguageChange={handleLanguageChange}
          cachedUserProfile={cachedUserProfile}
        />
        
        <main
          style={{
            flex: 1,
            padding: isMobile ? '16px 12px' : '40px 0px 0 0px',
            boxSizing: 'border-box',
            marginLeft: 0,
            marginRight: isMobile ? 0 : (sidebarVisible ? (sidebarOpen ? 0 : 0) : 0),
            width: '100%',
            minWidth: 0,
          }}
        >
          <div
            style={{
              paddingLeft: isMobile ? 0 : (sidebarVisible ? (isArabic ? 0 : 0) : 80),
              transition: 'padding-left 0.2s',
              maxWidth: '100%',
              overflowX: 'hidden',
            }}
          >
            {/* Header */}
            <PageHeader 
              title={isArabic ? 'الطلبات' : 'My Sessions'} 
              subtitle={isArabic ? 'إدارة الطلبات والجلسات السابقة' : 'Manage your requests and view past sessions'}
              isArabic={isArabic}
            />
              
            {/* Tab Navigation */}
            <div style={{ 
              display: 'flex', 
              gap: isMobile ? 8 : 12, 
              marginTop: isMobile ? 12 : 16,
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}>
              <button
                onClick={() => setActiveTab('pending')}
                style={{
                  padding: isMobile ? '8px 16px' : '10px 24px',
                  borderRadius: 12,
                  border: activeTab === 'pending' ? '2px solid #FF7426' : '2px solid #e0eaff',
                  background: activeTab === 'pending' ? '#fff6f0' : '#fff',
                  color: activeTab === 'pending' ? '#FF7426' : '#6B7280',
                  fontWeight: activeTab === 'pending' ? 700 : 500,
                  fontSize: isMobile ? 14 : 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: isMobile ? '1 1 auto' : 'none',
                  minWidth: isMobile ? 'calc(33.33% - 6px)' : 'auto'
                }}
              >
                {isArabic ? 'الطلبات المعلقة' : 'Pending Requests'}
              </button>
              <button
                onClick={() => setActiveTab('upcoming')}
                style={{
                  padding: isMobile ? '8px 16px' : '10px 24px',
                  borderRadius: 12,
                  border: activeTab === 'upcoming' ? '2px solid #FF7426' : '2px solid #e0eaff',
                  background: activeTab === 'upcoming' ? '#fff6f0' : '#fff',
                  color: activeTab === 'upcoming' ? '#FF7426' : '#6B7280',
                  fontWeight: activeTab === 'upcoming' ? 700 : 500,
                  fontSize: isMobile ? 14 : 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: isMobile ? '1 1 auto' : 'none',
                  minWidth: isMobile ? 'calc(33.33% - 6px)' : 'auto'
                }}
              >
                {isArabic ? 'الجلسات القادمة' : 'Upcoming Sessions'}
              </button>
              <button
                onClick={() => setActiveTab('past')}
                style={{
                  padding: isMobile ? '8px 16px' : '10px 24px',
                  borderRadius: 12,
                  border: activeTab === 'past' ? '2px solid #FF7426' : '2px solid #e0eaff',
                  background: activeTab === 'past' ? '#fff6f0' : '#fff',
                  color: activeTab === 'past' ? '#FF7426' : '#6B7280',
                  fontWeight: activeTab === 'past' ? 700 : 500,
                  fontSize: isMobile ? 14 : 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flex: isMobile ? '1 1 auto' : 'none',
                  minWidth: isMobile ? 'calc(33.33% - 6px)' : 'auto'
                }}
              >
                {isArabic ? 'الجلسات السابقة' : 'Past Sessions'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              color: '#DC2626',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 24,
              fontSize: 14
            }}>
              {error}
            </div>
          )}

          {testMessage && <div style={{background:'#ffb400',color:'#232B3E',padding:12,borderRadius:8,marginBottom:16,fontWeight:700}}>{testMessage}</div>}

          {/* Pending Requests Tab */}
          {activeTab === 'pending' && (
            <>
              {requests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6B7280',
                  fontSize: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <FaClipboardList size={48} style={{ color: '#9CA3AF', marginBottom: 16 }} />
                  <div>{isArabic ? 'لم يتم العثور على أي طلبات' : 'No requests found'}</div>
                  <div style={{ fontSize: 14, marginTop: 8 }}>
                    {isArabic ? 'لم ترسل أي طلبات جلسات للمعلمين بعد' : "You haven't sent any session requests to teachers yet."}
                  </div>
                </div>
              ) : (
            <div style={{
              display: 'grid',
              gap: isMobile ? 16 : 20,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
              marginTop: isMobile ? 16 : 0
            }}>
              {requests.map((request) => (
                <div
                  key={request.id}
                  style={{
                    background: '#fff',
                    borderRadius: isMobile ? 12 : 16,
                    padding: isMobile ? 16 : 24,
                    boxShadow: '0 2px 8px #e0eaff',
                    border: '1px solid #e0eaff',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: isMobile ? 'none' : 'box-shadow 0.2s, transform 0.2s',
                  }}
                  onClick={() => !isMobile && alert(`Request details:\nTeacher: ${request.teacher_name}\nDate: ${formatDate(request.date)}\nStatus: ${request.status}`)}
                  onMouseOver={e => {
                    if (!isMobile) {
                      e.currentTarget.style.boxShadow = '0 6px 24px #cbe7ff';
                      e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
                    }
                  }}
                  onMouseOut={e => {
                    if (!isMobile) {
                      e.currentTarget.style.boxShadow = '0 2px 8px #e0eaff';
                      e.currentTarget.style.transform = 'none';
                    }
                  }}
                >
                  {request.reschedule_status === 'pending' && (
                    <div style={{
                      background: '#fff4e6',
                      border: '1px solid #ffddb0',
                      color: '#b45309',
                      padding: isMobile ? '6px 10px' : '8px 12px',
                      borderRadius: isMobile ? 8 : 10,
                      fontWeight: 600,
                      marginBottom: isMobile ? 10 : 12,
                      fontSize: isMobile ? 12 : 13
                    }}>
                      <div style={{ fontSize: isMobile ? 12 : 13 }}>
                        {isArabic ? 'طلب إعادة الجدولة قيد انتظار موافقة المعلم' : 'Reschedule request pending teacher approval'}
                      </div>
                      {request.reschedule_requested_date && (
                        <div style={{ marginTop: isMobile ? 4 : 6, fontSize: isMobile ? 11 : 12 }}>
                          {(isArabic ? 'الموعد المقترح: ' : 'Proposed time: ') + `${request.reschedule_requested_date} • ${request.reschedule_requested_time_from} - ${request.reschedule_requested_time_to}`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reschedule button - only for eligible bookings */}
                  {request.status === 'accepted' && !request.reschedule_status && (
                    <div style={{ marginBottom: isMobile ? 10 : 12 }}>
                      {isRescheduleEligible(request) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRescheduleBooking(request);
                            setShowRescheduleModal(true);
                            setRescheduleForm({ newDate: '', newTimeFrom: '', newTimeTo: '', reason: '' });
                            setRescheduleError('');
                          }}
                          style={{
                            padding: isMobile ? '8px 16px' : '10px 20px',
                            background: '#FF7426',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: isMobile ? 13 : 14,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          🔄 {isArabic ? 'طلب إعادة الجدولة' : 'Request Reschedule'}
                        </button>
                      ) : (
                        <div style={{
                          padding: isMobile ? '6px 12px' : '8px 16px',
                          background: '#f5f5f5',
                          borderRadius: 8,
                          color: '#999',
                          fontSize: isMobile ? 12 : 13
                        }}>
                          <FaInfoCircle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {getRescheduleIneligibilityReason(request)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Remove Button - only show for request creator */}
                  {request.student_email === studentEmail && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveRequest(request.id);
                      }}
                      style={{
                        position: 'absolute',
                        top: isMobile ? 8 : 12,
                        right: isMobile ? 8 : 12,
                        background: 'rgba(255,255,255,0.9)',
                        border: '1px solid #e5e7eb',
                        color: '#6B7280',
                        cursor: 'pointer',
                        padding: isMobile ? 6 : 8,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        zIndex: 10
                      }}
                      title="Remove request"
                    >
                      <FaTimes size={isMobile ? 14 : 16} />
                    </button>
                  )}

                  {/* Group Participant Badge */}
                  {request.request_type === 'group' && request.student_email !== studentEmail && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: '#EDE9FE',
                      color: '#7C3AED',
                      padding: '6px 12px',
                      borderRadius: 20,
                      fontSize: isMobile ? 11 : 12,
                      fontWeight: 600,
                      marginBottom: isMobile ? 10 : 12
                    }}>
                      <FaUsers size={isMobile ? 12 : 14} />
                      {isArabic ? 'أنت مشارك في هذه الجلسة' : 'You\'re a participant'}
                      <span style={{ color: '#9CA3AF', fontWeight: 400 }}>
                        ({isArabic ? 'منظم:' : 'Organizer:'} {request.student_name})
                      </span>
                    </div>
                  )}

                  {/* Teacher Info */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 12 : 16 }}>
                    <img
                      src={teacherProfiles[request.teacher_email] || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                      alt={request.teacher_name}
                      style={{
                        width: isMobile ? 48 : 56,
                        height: isMobile ? 48 : 56,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        marginRight: isMobile ? 10 : 12,
                        border: isMobile ? '2px solid #e0eaff' : '3px solid #e0eaff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        flexShrink: 0
                      }}
                      onError={(e) => {
                        e.target.src = 'https://www.gravatar.com/avatar/?d=mp&f=y';
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: 700, 
                        fontSize: isMobile ? 16 : 18, 
                        color: '#232B3E',
                        wordBreak: 'break-word'
                      }}>
                        {request.teacher_name}
                      </div>
                      <div style={{ 
                        color: '#10b981', 
                        fontSize: isMobile ? 12 : 13, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 4,
                        flexWrap: 'wrap'
                      }}>
                        <FaCheck size={14} style={{ marginRight: 4 }} />
                        <span>{isArabic ? 'معلم معتمد على المنصة' : 'Verified Platform Teacher'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Request Details */}
                  <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? 6 : 8,
                      flexWrap: 'wrap',
                      gap: isMobile ? 4 : 0
                    }}>
                      <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Date:</span>
                      <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14, textAlign: 'right' }}>
                        {isMobile ? formatDateShort(request.date) : formatDate(request.date)}
                      </span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? 6 : 8,
                      flexWrap: 'wrap',
                      gap: isMobile ? 4 : 0
                    }}>
                      <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Time:</span>
                      <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14 }}>
                        {formatTime(request.time_from)} - {formatTime(request.time_to)}
                      </span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? 6 : 8,
                      flexWrap: 'wrap',
                      gap: isMobile ? 4 : 0
                    }}>
                      <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Place:</span>
                      <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14, textAlign: 'right', wordBreak: 'break-word' }}>
                        {request.place}
                      </span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? 6 : 8,
                      flexWrap: 'wrap',
                      gap: isMobile ? 4 : 0
                    }}>
                      <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Type:</span>
                      <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14, textTransform: 'capitalize' }}>
                        {request.request_type}
                      </span>
                    </div>
                    {request.num_students && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginBottom: isMobile ? 6 : 8,
                        flexWrap: 'wrap',
                        gap: isMobile ? 4 : 0
                      }}>
                        <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Students:</span>
                        <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14 }}>
                          {request.num_students}
                        </span>
                      </div>
                    )}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? 6 : 8,
                      flexWrap: 'wrap',
                      gap: isMobile ? 4 : 0
                    }}>
                      <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Total Price:</span>
                      <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14 }}>
                        {request.total_price} BD
                      </span>
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    borderRadius: 8,
                    background: `${getStatusColor(request.status, request.payment_status)}15`,
                    border: `1px solid ${getStatusColor(request.status, request.payment_status)}30`,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    gap: isMobile ? 8 : 0
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ color: getStatusColor(request.status, request.payment_status) }}>
                        {getStatusIcon(request.status, request.payment_status)}
                      </div>
                      <span style={{
                        fontWeight: 600,
                        color: getStatusColor(request.status, request.payment_status),
                        fontSize: isMobile ? 13 : 14
                      }}>
                        {getStatusLabel(request.status, request.payment_status, isArabic)}
                      </span>
                    </div>
                    <div style={{ fontSize: isMobile ? 11 : 12, color: '#6B7280' }}>
                      {isMobile ? formatDateShort(request.created_at) : formatDate(request.created_at)}
                    </div>
                  </div>
                  {userRole === 'teacher' && request.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                      <button
                        style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 15, cursor: actionLoading === request.id + '-accepted' ? 'wait' : 'pointer', opacity: actionLoading === request.id + '-accepted' ? 0.7 : 1 }}
                        disabled={actionLoading === request.id + '-accepted'}
                        onClick={() => handleAction(request.id, 'accepted')}
                      >
                        {actionLoading === request.id + '-accepted' ? 'Accepting...' : 'Accept'}
                      </button>
                      <button
                        style={{ background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 15, cursor: actionLoading === request.id + '-rejected' ? 'wait' : 'pointer', opacity: actionLoading === request.id + '-rejected' ? 0.7 : 1 }}
                        disabled={actionLoading === request.id + '-rejected'}
                        onClick={() => handleAction(request.id, 'rejected')}
                      >
                        {actionLoading === request.id + '-rejected' ? 'Rejecting...' : 'Reject'}
                      </button>
                    </div>
                  )}
                  {actionError && <div style={{color:'#d32f2f',marginTop:8,fontWeight:600}}>{actionError}</div>}
                  {actionSuccess && <div style={{color:'#1a7f37',marginTop:8,fontWeight:600}}>{actionSuccess}</div>}
                </div>
              ))}
            </div>
              )}
            </>
          )}

          {/* Upcoming Sessions Tab */}
          {activeTab === 'upcoming' && (
            <>
              {upcomingSessions.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6B7280',
                  fontSize: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <FaCalendarAlt size={48} style={{ color: '#9CA3AF', marginBottom: 16 }} />
                  <div>{isArabic ? 'لا توجد جلسات قادمة' : 'No upcoming sessions'}</div>
                  <div style={{ fontSize: 14, marginTop: 8 }}>
                    {isArabic ? 'ليس لديك أي جلسات مجدولة' : "You don't have any scheduled sessions."}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gap: isMobile ? 16 : 20,
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
                  marginTop: isMobile ? 16 : 24
                }}>
                  {upcomingSessions.map((session) => (
                    <div
                      key={session.id}
                      style={{
                        background: '#fff',
                        borderRadius: isMobile ? 12 : 16,
                        padding: isMobile ? 16 : 24,
                        boxShadow: '0 2px 8px #e0eaff',
                        border: '2px solid #10B981',
                      }}
                    >
                      {/* Group Participant Badge */}
                      {session.request_type === 'group' && session.student_email !== studentEmail && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: '#EDE9FE',
                          color: '#7C3AED',
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: isMobile ? 11 : 12,
                          fontWeight: 600,
                          marginBottom: isMobile ? 10 : 12
                        }}>
                          <FaUsers size={isMobile ? 12 : 14} />
                          {isArabic ? 'أنت مشارك في هذه الجلسة' : 'You\'re a participant'}
                          <span style={{ color: '#9CA3AF', fontWeight: 400 }}>
                            ({isArabic ? 'منظم:' : 'Organizer:'} {session.student_name})
                          </span>
                        </div>
                      )}

                      {/* Teacher Info */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 12 : 16 }}>
                        <img
                          src={teacherProfiles[session.teacher_email] || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                          alt={session.teacher_name}
                          style={{
                            width: isMobile ? 48 : 56,
                            height: isMobile ? 48 : 56,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: isMobile ? 10 : 12,
                            border: isMobile ? '2px solid #10B981' : '3px solid #10B981',
                            boxShadow: '0 2px 8px rgba(16,185,129,0.2)',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.src = 'https://www.gravatar.com/avatar/?d=mp&f=y';
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: isMobile ? 16 : 18, color: '#232B3E', wordBreak: 'break-word' }}>
                            {session.teacher_name}
                          </div>
                          <div style={{ color: '#10B981', fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>
                            {formatDate(session.date)} • {formatTime(session.time_from)}
                          </div>
                        </div>
                      </div>

                      {/* Session Details */}
                      <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: isMobile ? 6 : 8,
                          flexWrap: 'wrap',
                          gap: isMobile ? 4 : 0
                        }}>
                          <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>{isArabic ? 'المادة:' : 'Subject:'}</span>
                          <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14, textAlign: 'right' }}>
                            {session.subject || 'N/A'}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: isMobile ? 6 : 8,
                          flexWrap: 'wrap',
                          gap: isMobile ? 4 : 0
                        }}>
                          <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>{isArabic ? 'المدة:' : 'Duration:'}</span>
                          <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14 }}>
                            {formatTime(session.time_from)} - {formatTime(session.time_to)}
                          </span>
                        </div>
                        {session.request_type && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: isMobile ? 4 : 0
                          }}>
                            <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>{isArabic ? 'النوع:' : 'Type:'}</span>
                            <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14 }}>
                              {session.request_type === 'one-on-one' ? (isArabic ? 'فردي' : 'One-on-One') : (isArabic ? 'مجموعة' : 'Group')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Reschedule button for upcoming sessions */}
                      {session.reschedule_status === 'pending' ? (
                        <div style={{
                          background: '#fff4e6',
                          border: '1px solid #ffddb0',
                          color: '#b45309',
                          padding: isMobile ? '8px 12px' : '10px 16px',
                          borderRadius: 8,
                          fontWeight: 600,
                          marginBottom: isMobile ? 10 : 12,
                          fontSize: isMobile ? 12 : 13
                        }}>
                          <div style={{ fontSize: isMobile ? 12 : 13 }}>
                            {isArabic ? 'طلب إعادة الجدولة قيد انتظار موافقة المعلم' : 'Reschedule request pending teacher approval'}
                          </div>
                          {session.reschedule_requested_date && (
                            <div style={{ marginTop: isMobile ? 4 : 6, fontSize: isMobile ? 11 : 12 }}>
                              {(isArabic ? 'الموعد المقترح: ' : 'Proposed time: ') + `${session.reschedule_requested_date} • ${session.reschedule_requested_time_from} - ${session.reschedule_requested_time_to}`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {isRescheduleEligible(session) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRescheduleBooking(session);
                                setShowRescheduleModal(true);
                                setRescheduleForm({ newDate: '', newTimeFrom: '', newTimeTo: '', reason: '' });
                                setRescheduleError('');
                              }}
                              style={{
                                width: '100%',
                                padding: isMobile ? '10px 16px' : '12px 20px',
                                background: '#FF7426',
                                color: 'white',
                                border: 'none',
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: isMobile ? 13 : 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                marginBottom: isMobile ? 10 : 12
                              }}
                            >
                              🔄 {isArabic ? 'طلب إعادة الجدولة' : 'Request Reschedule'}
                            </button>
                          ) : getRescheduleIneligibilityReason(session) ? (
                            <div style={{
                              padding: isMobile ? '8px 12px' : '10px 16px',
                              background: '#f5f5f5',
                              borderRadius: 8,
                              color: '#999',
                              fontSize: isMobile ? 12 : 13,
                              marginBottom: isMobile ? 10 : 12,
                              textAlign: 'center'
                            }}>
                              <FaInfoCircle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {getRescheduleIneligibilityReason(session)}
                            </div>
                          ) : null}
                        </>
                      )}

                      {/* Meeting Link */}
                      {session.meeting_link && (
                        <a
                          href={session.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            background: '#10B981',
                            color: '#fff',
                            padding: isMobile ? '10px' : '12px',
                            borderRadius: 10,
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: isMobile ? 14 : 15,
                            textDecoration: 'none',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#059669'}
                          onMouseOut={(e) => e.target.style.background = '#10B981'}
                        >
                          <FaVideo style={{ marginRight: 8, verticalAlign: 'middle' }} />
                          {isArabic ? 'انضم إلى الجلسة' : 'Join Session'}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Past Sessions Tab */}
          {activeTab === 'past' && (
            <>
              {pastSessions.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6B7280',
                  fontSize: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <FaGraduationCap size={48} style={{ color: '#9CA3AF', marginBottom: 16 }} />
                  <div>{isArabic ? 'لا توجد جلسات سابقة' : 'No past sessions found'}</div>
                  <div style={{ fontSize: 14, marginTop: 8 }}>
                    {isArabic ? 'لم تكمل أي جلسات بعد' : "You haven't completed any sessions yet."}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gap: isMobile ? 16 : 20,
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
                  marginTop: isMobile ? 0 : 0
                }}>
                  {pastSessions.map((session) => (
                    <div
                      key={session.id}
                      style={{
                        background: '#fff',
                        borderRadius: isMobile ? 12 : 16,
                        padding: isMobile ? 16 : 24,
                        boxShadow: '0 2px 8px #e0eaff',
                        border: '1px solid #e0eaff',
                      }}
                    >
                      {/* Group Participant Badge */}
                      {session.request_type === 'group' && session.student_email !== studentEmail && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: '#EDE9FE',
                          color: '#7C3AED',
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: isMobile ? 11 : 12,
                          fontWeight: 600,
                          marginBottom: isMobile ? 10 : 12
                        }}>
                          <FaUsers size={isMobile ? 12 : 14} />
                          {isArabic ? 'أنت مشارك في هذه الجلسة' : 'You\'re a participant'}
                          <span style={{ color: '#9CA3AF', fontWeight: 400 }}>
                            ({isArabic ? 'منظم:' : 'Organizer:'} {session.student_name})
                          </span>
                        </div>
                      )}

                      {/* Teacher Info - Clickable */}
                      <div 
                        onClick={() => setSelectedSession(session)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: isMobile ? 12 : 16,
                          cursor: 'pointer',
                          padding: isMobile ? 8 : 12,
                          borderRadius: 8,
                          transition: 'background-color 0.2s',
                          ':hover': { backgroundColor: '#f8f9fa' }
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <img
                          src={teacherProfiles[session.teacher_email] || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                          alt={session.teacher_name}
                          style={{
                            width: isMobile ? 48 : 56,
                            height: isMobile ? 48 : 56,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: isMobile ? 10 : 12,
                            border: isMobile ? '2px solid #e0eaff' : '3px solid #e0eaff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.src = 'https://www.gravatar.com/avatar/?d=mp&f=y';
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: isMobile ? 16 : 18, color: '#232B3E', wordBreak: 'break-word' }}>
                            {session.teacher_name}
                          </div>
                          <div style={{ color: '#6B7280', fontSize: isMobile ? 12 : 14 }}>
                            {isMobile ? formatDateShort(session.date) : formatDate(session.date)} • {formatTime(session.time_from)}
                          </div>
                        </div>
                        {(session.recording_url || session.ai_companion_summary || session.ai_summary) && (
                          <div style={{ 
                            marginLeft: 8,
                            color: '#667eea',
                            fontSize: isMobile ? 12 : 14,
                            fontWeight: 600
                          }}>
                            {isArabic ? 'عرض التفاصيل' : 'View Details'} →
                          </div>
                        )}
                      </div>

                      {/* Session Details */}
                      <div style={{ marginBottom: isMobile ? 12 : 16 }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: isMobile ? 6 : 8,
                          flexWrap: 'wrap',
                          gap: isMobile ? 4 : 0
                        }}>
                          <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Subject:</span>
                          <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14, textAlign: 'right' }}>
                            {session.subject || 'N/A'}
                          </span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: isMobile ? 6 : 8,
                          flexWrap: 'wrap',
                          gap: isMobile ? 4 : 0
                        }}>
                          <span style={{ color: '#6B7280', fontSize: isMobile ? 13 : 14 }}>Duration:</span>
                          <span style={{ fontWeight: 600, color: '#232B3E', fontSize: isMobile ? 13 : 14 }}>
                            {formatTime(session.time_from)} - {formatTime(session.time_to)}
                          </span>
                        </div>
                      </div>

                      {/* Recording & AI Summary Section */}
                      {session.recording_url && (
                        <div style={{
                          background: '#f8f8fa',
                          borderRadius: isMobile ? 10 : 12,
                          padding: isMobile ? 12 : 16,
                          marginTop: isMobile ? 12 : 16
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isMobile ? 10 : 12 }}>
                            <FaVideo size={isMobile ? 14 : 16} color="#667eea" />
                            <span style={{ fontWeight: 700, color: '#232B3E', fontSize: isMobile ? 14 : 16 }}>
                              {isArabic ? 'تسجيل الجلسة' : 'Session Recording'}
                            </span>
                          </div>
                          <a
                            href={session.recording_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              background: '#667eea',
                              color: '#fff',
                              padding: isMobile ? '8px 16px' : '10px 20px',
                              borderRadius: 8,
                              textDecoration: 'none',
                              fontWeight: 600,
                              fontSize: isMobile ? 13 : 14,
                              marginBottom: 12,
                              width: isMobile ? '100%' : 'auto',
                              justifyContent: 'center'
                            }}
                          >
                            <FaDownload size={isMobile ? 12 : 14} />
                            {isArabic ? 'شاهد / حمل الفيديو' : 'Watch / Download Video'}
                          </a>
                        </div>
                      )}

                      {/* Platform-Only Features Badge */}
                      <div style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        borderRadius: 8,
                        padding: isMobile ? '8px 12px' : '10px 14px',
                        marginTop: isMobile ? 10 : 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? 6 : 8,
                        color: '#fff',
                        fontSize: isMobile ? 11 : 12,
                        fontWeight: 600,
                        flexWrap: 'wrap'
                      }}>
                        <FaLock size={isMobile ? 10 : 12} />
                        <span>{isArabic ? 'ميزات حصرية للمنصة' : 'Platform Exclusive Features'}</span>
                      </div>

                      {/* AI Summary */}
                      {session.ai_summary && (
                        <div style={{
                          background: '#fff8ed',
                          borderRadius: isMobile ? 10 : 12,
                          padding: isMobile ? 12 : 16,
                          marginTop: isMobile ? 10 : 12,
                          border: '1px solid #ffd6a5'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: isMobile ? 6 : 8, 
                            marginBottom: isMobile ? 6 : 8,
                            flexWrap: 'wrap'
                          }}>
                            <FaFileAlt size={isMobile ? 14 : 16} color="#FF7426" />
                            <span style={{ fontWeight: 700, color: '#232B3E', fontSize: isMobile ? 14 : 16 }}>
                              {isArabic ? 'ملخص الذكاء الاصطناعي' : 'AI Summary'}
                            </span>
                            <span style={{
                              background: '#FF7426',
                              color: '#fff',
                              fontSize: isMobile ? 9 : 10,
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontWeight: 700,
                              marginLeft: isMobile ? 0 : 8
                            }}>
                              {isArabic ? 'حصري' : 'EXCLUSIVE'}
                            </span>
                          </div>
                          <div style={{
                            color: '#555',
                            fontSize: isMobile ? 13 : 14,
                            lineHeight: 1.6,
                            marginBottom: isMobile ? 6 : 8
                          }}>
                            {session.ai_summary_short || session.ai_summary}
                          </div>
                          {session.transcript && (
                            <button
                              onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                              style={{
                                background: '#FF7426',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                padding: isMobile ? '6px 12px' : '8px 16px',
                                fontSize: isMobile ? 12 : 13,
                                fontWeight: 600,
                                cursor: 'pointer',
                                marginTop: isMobile ? 6 : 8,
                                width: isMobile ? '100%' : 'auto'
                              }}
                            >
                              {expandedSession === session.id 
                                ? (isArabic ? 'إخفاء النص الكامل' : 'Hide Full Transcript')
                                : (isArabic ? 'عرض النص الكامل' : 'View Full Transcript')}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Full Transcript (Expandable) */}
                      {expandedSession === session.id && session.transcript && (
                        <div style={{
                          background: '#f5f5f5',
                          borderRadius: 12,
                          padding: 16,
                          marginTop: 12,
                          maxHeight: 300,
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: 8, color: '#232B3E' }}>
                            {isArabic ? 'النص الكامل' : 'Full Transcript'}
                          </div>
                          <div style={{
                            color: '#555',
                            fontSize: 13,
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap'
                          }}>
                            {session.transcript}
                          </div>
                        </div>
                      )}

                      {/* Quick Rebook Button */}
                      <div style={{ marginTop: isMobile ? 12 : 16, paddingTop: isMobile ? 12 : 16, borderTop: '1px solid #e5e7eb' }}>
                        <div style={{
                          background: '#f0f9ff',
                          borderRadius: isMobile ? 8 : 10,
                          padding: isMobile ? 10 : 12,
                          marginBottom: isMobile ? 10 : 12,
                          border: '1px solid #bae6fd'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, marginBottom: isMobile ? 6 : 8 }}>
                            <FaGift size={isMobile ? 14 : 16} color="#3b82f6" />
                            <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#1e40af' }}>
                              {isArabic ? 'مكافأة الحجز المتكرر' : 'Repeat Booking Reward'}
                            </span>
                          </div>
                          <div style={{ fontSize: isMobile ? 11 : 12, color: '#475569', marginBottom: isMobile ? 8 : 10 }}>
                            {isArabic 
                              ? 'احصل على خصم 5% عند الحجز مرة أخرى عبر المنصة'
                              : 'Get 5% discount when you book again through the platform'}
                          </div>
                          <button
                            onClick={async () => {
                              // Fetch teacher data and navigate to calendar
                              try {
                                const { data: teacherData } = await supabase
                                  .from('Teachers')
                                  .select('*')
                                  .eq('email', session.teacher_email)
                                  .single();
                                
                                if (teacherData) {
                                  navigate('/dashboard/student/calendar', { 
                                    state: { 
                                      teacher: teacherData,
                                      requestType: session.request_type || 'individual'
                                    } 
                                  });
                                } else {
                                  alert(isArabic ? 'لم يتم العثور على المعلم' : 'Teacher not found');
                                }
                              } catch (error) {
                                console.error('Error fetching teacher:', error);
                                alert(isArabic ? 'حدث خطأ أثناء جلب بيانات المعلم' : 'Error fetching teacher data');
                              }
                            }}
                            style={{
                              width: '100%',
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: isMobile ? '10px 16px' : '12px 20px',
                              fontSize: isMobile ? 13 : 14,
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                              transition: isMobile ? 'none' : 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (!isMobile) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isMobile) {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,130,246,0.3)';
                              }
                            }}
                          >
                            <FaRedo size={isMobile ? 12 : 14} />
                            {isArabic ? 'حجز سريع مرة أخرى' : 'Quick Rebook'}
                          </button>
                        </div>
                        
                        {/* Platform Warning */}
                        <div style={{
                          background: '#fef3c7',
                          borderRadius: isMobile ? 6 : 8,
                          padding: isMobile ? 8 : 10,
                          border: '1px solid #fcd34d',
                          fontSize: isMobile ? 10 : 11,
                          color: '#92400e',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: isMobile ? 6 : 8
                        }}>
                          <FaLock size={isMobile ? 10 : 12} style={{ marginTop: 2, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: 2, fontSize: isMobile ? 11 : 12 }}>
                              {isArabic ? 'تذكير مهم' : 'Important Reminder'}
                            </div>
                            <div style={{ fontSize: isMobile ? 10 : 11 }}>
                              {isArabic 
                                ? 'جميع الحجوزات يجب أن تتم عبر المنصة للحصول على الحماية والميزات الحصرية.'
                                : 'All bookings must be made through the platform to receive protection and exclusive features.'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: isMobile ? '10px 12px' : '12px 16px',
                        borderRadius: 8,
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        marginTop: isMobile ? 12 : 16
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8 }}>
                          <FaCheck size={isMobile ? 14 : 16} color="#10B981" />
                          <span style={{ fontWeight: 600, color: '#10B981', fontSize: isMobile ? 13 : 14 }}>
                            {isArabic ? 'مكتمل' : 'Completed'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleBooking && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: isMobile ? 20 : 30,
            maxWidth: 500,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              marginBottom: 20,
              color: '#232B3E',
              fontSize: isMobile ? 18 : 20
            }}>
              {isArabic ? 'طلب إعادة الجدولة' : 'Request Reschedule'}
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
                {isArabic ? 'التاريخ الجديد' : 'New Date'}
              </label>
              <input
                type="date"
                value={rescheduleForm.newDate}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, newDate: e.target.value })}
                min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
                {isArabic ? 'وقت البداية' : 'Start Time'}
              </label>
              <input
                type="time"
                value={rescheduleForm.newTimeFrom}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, newTimeFrom: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
                {isArabic ? 'وقت الانتهاء' : 'End Time'}
              </label>
              <input
                type="time"
                value={rescheduleForm.newTimeTo}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, newTimeTo: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
                {isArabic ? 'السبب (اختياري)' : 'Reason (Optional)'}
              </label>
              <textarea
                value={rescheduleForm.reason}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, reason: e.target.value })}
                placeholder={isArabic ? 'اشرح سبب إعادة الجدولة...' : 'Explain why you need to reschedule...'}
                maxLength={500}
                rows={3}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14,
                  resize: 'vertical'
                }}
              />
            </div>

            {rescheduleError && (
              <div style={{
                background: '#fee',
                color: '#c33',
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 13
              }}>
                {rescheduleError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRescheduleModal(false)}
                disabled={rescheduleLoading}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  color: '#666',
                  border: 'none',
                  borderRadius: 8,
                  cursor: rescheduleLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14
                }}
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={async () => {
                  setRescheduleLoading(true);
                  setRescheduleError('');

                  try {
                    // Validate fields
                    if (!rescheduleForm.newDate || !rescheduleForm.newTimeFrom || !rescheduleForm.newTimeTo) {
                      throw new Error(isArabic ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
                    }

                    // Validate 24-hour rule
                    const sessionDateTime = new Date(`${rescheduleForm.newDate} ${rescheduleForm.newTimeFrom}`);
                    const now = new Date();
                    const hoursUntilSession = (sessionDateTime - now) / (1000 * 60 * 60);

                    if (hoursUntilSession < 24) {
                      throw new Error(
                        isArabic
                          ? `يجب أن يكون الموعد بعد 24 ساعة على الأقل (${Math.round(hoursUntilSession)} ساعة متبقية)`
                          : `New time must be at least 24 hours away (${Math.round(hoursUntilSession)} hours remaining)`
                      );
                    }

                    // Convert times to 12-hour format for API
                    const timeFrom12 = convertTo12Hour(rescheduleForm.newTimeFrom);
                    const timeTo12 = convertTo12Hour(rescheduleForm.newTimeTo);

                    // Call API
                    await requestReschedule(rescheduleBooking.id, {
                      studentId: rescheduleBooking.student_id,
                      newDate: rescheduleForm.newDate,
                      newTimeFrom: timeFrom12,
                      newTimeTo: timeTo12,
                      reason: rescheduleForm.reason
                    });

                    setShowRescheduleModal(false);
                    fetchRequests(); // Refresh list

                    alert(
                      isArabic
                        ? 'تم إرسال طلب إعادة الجدولة بنجاح! سيتم إخطار المعلم عبر البريد الإلكتروني.'
                        : 'Reschedule request sent successfully! The teacher will be notified via email.'
                    );
                  } catch (err) {
                    setRescheduleError(err.message || (isArabic ? 'فشل إرسال الطلب' : 'Failed to send request'));
                  } finally {
                    setRescheduleLoading(false);
                  }
                }}
                disabled={rescheduleLoading}
                style={{
                  padding: '10px 20px',
                  background: rescheduleLoading ? '#ccc' : '#FF7426',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: rescheduleLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14
                }}
              >
                {rescheduleLoading
                  ? (isArabic ? 'جارٍ الإرسال...' : 'Sending...')
                  : (isArabic ? 'إرسال الطلب' : 'Send Request')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal - Recording & Summary */}
      {selectedSession && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '16px' : '20px',
          overflowY: 'auto'
        }}
        onClick={() => setSelectedSession(null)}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: isMobile ? 16 : 20,
              padding: isMobile ? 20 : 32,
              maxWidth: isMobile ? '100%' : '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedSession(null)}
              style={{
                position: 'absolute',
                top: isMobile ? 16 : 20,
                right: isMobile ? 16 : 20,
                background: '#f5f5f5',
                border: 'none',
                borderRadius: '50%',
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 20,
                color: '#666',
                zIndex: 10
              }}
            >
              <FaTimes />
            </button>

            {/* Teacher Info Header */}
            <div style={{ marginBottom: isMobile ? 20 : 24, paddingRight: isMobile ? 40 : 50 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 8 : 12 }}>
                <img
                  src={teacherProfiles[selectedSession.teacher_email] || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                  alt={selectedSession.teacher_name}
                  style={{
                    width: isMobile ? 56 : 64,
                    height: isMobile ? 56 : 64,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #e0eaff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://www.gravatar.com/avatar/?d=mp&f=y';
                  }}
                />
                <div>
                  <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#232B3E' }}>
                    {selectedSession.teacher_name}
                  </h2>
                  <div style={{ color: '#6B7280', fontSize: isMobile ? 14 : 16, marginTop: 4 }}>
                    {formatDate(selectedSession.date)} • {formatTime(selectedSession.time_from)} - {formatTime(selectedSession.time_to)}
                  </div>
                  {selectedSession.subject && (
                    <div style={{ 
                      display: 'inline-block',
                      background: '#667eea',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: 6,
                      fontSize: isMobile ? 12 : 13,
                      fontWeight: 600,
                      marginTop: 8
                    }}>
                      {selectedSession.subject}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Session Recording */}
            {selectedSession.recording_url ? (
              <div style={{
                background: '#f8f9fa',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 20,
                marginBottom: isMobile ? 16 : 20,
                border: '1px solid #e0eaff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isMobile ? 12 : 16 }}>
                  <FaVideo size={isMobile ? 18 : 20} color="#667eea" />
                  <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#232B3E' }}>
                    {isArabic ? 'تسجيل الجلسة' : 'Session Recording'}
                  </h3>
                </div>
                <a
                  href={selectedSession.recording_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#667eea',
                    color: '#fff',
                    padding: isMobile ? '12px 20px' : '14px 24px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: isMobile ? 14 : 15,
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5568d3'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
                >
                  <FaDownload size={isMobile ? 14 : 16} />
                  {isArabic ? 'شاهد / حمل الفيديو' : 'Watch / Download Video'}
                </a>
              </div>
            ) : (
              <div style={{
                background: '#fff8ed',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 20,
                marginBottom: isMobile ? 16 : 20,
                border: '1px solid #ffd6a5',
                textAlign: 'center'
              }}>
                <FaVideo size={isMobile ? 24 : 28} color="#FF7426" style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, color: '#666', fontSize: isMobile ? 14 : 15 }}>
                  {isArabic ? 'التسجيل غير متاح حالياً' : 'Recording not available yet'}
                </p>
              </div>
            )}

            {/* AI Summary */}
            {(selectedSession.ai_companion_summary || selectedSession.ai_summary) ? (
              <div style={{
                background: '#fff8ed',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 20,
                marginBottom: isMobile ? 16 : 20,
                border: '1px solid #ffd6a5'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10, 
                  marginBottom: isMobile ? 12 : 16,
                  flexWrap: 'wrap'
                }}>
                  <FaFileAlt size={isMobile ? 18 : 20} color="#FF7426" />
                  <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#232B3E', flex: 1 }}>
                    {isArabic ? 'ملخص الذكاء الاصطناعي' : 'AI Summary'}
                  </h3>
                  <span style={{
                    background: '#FF7426',
                    color: '#fff',
                    fontSize: isMobile ? 10 : 11,
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontWeight: 700
                  }}>
                    {isArabic ? 'حصري' : 'EXCLUSIVE'}
                  </span>
                </div>
                <div style={{
                  color: '#555',
                  fontSize: isMobile ? 14 : 15,
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  marginBottom: selectedSession.ai_companion_transcript || selectedSession.transcript ? 16 : 0
                }}>
                  {selectedSession.ai_companion_summary || selectedSession.ai_summary}
                </div>

                {/* Action Items */}
                {selectedSession.ai_companion_action_items && selectedSession.ai_companion_action_items.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #ffd6a5' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: isMobile ? 14 : 15, fontWeight: 700, color: '#232B3E' }}>
                      {isArabic ? 'المهام والإجراءات' : 'Action Items & Homework'}
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: 20, color: '#555', fontSize: isMobile ? 13 : 14, lineHeight: 1.8 }}>
                      {selectedSession.ai_companion_action_items.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: 8 }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Transcript */}
                {(selectedSession.ai_companion_transcript || selectedSession.transcript) && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #ffd6a5' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: isMobile ? 14 : 15, fontWeight: 700, color: '#232B3E' }}>
                      {isArabic ? 'النص الكامل' : 'Full Transcript'}
                    </h4>
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: 8,
                      padding: isMobile ? 12 : 16,
                      maxHeight: 300,
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      color: '#555',
                      fontSize: isMobile ? 13 : 14,
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedSession.ai_companion_transcript || selectedSession.transcript}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                background: '#fff8ed',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 20,
                marginBottom: isMobile ? 16 : 20,
                border: '1px solid #ffd6a5',
                textAlign: 'center'
              }}>
                <FaFileAlt size={isMobile ? 24 : 28} color="#FF7426" style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, color: '#666', fontSize: isMobile ? 14 : 15 }}>
                  {isArabic ? 'الملخص غير متاح حالياً' : 'Summary not available yet'}
                </p>
              </div>
            )}

            {/* Session Insights */}
            {selectedSession.ai_companion_insights && (
              <div style={{
                background: '#f0f9ff',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 20,
                marginBottom: isMobile ? 16 : 20,
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#232B3E' }}>
                  {isArabic ? 'رؤى الجلسة' : 'Session Insights'}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
                  {selectedSession.ai_companion_insights.studentProgress && (
                    <div>
                      <div style={{ fontSize: isMobile ? 12 : 13, color: '#6B7280', marginBottom: 4 }}>
                        {isArabic ? 'تقدم الطالب' : 'Student Progress'}
                      </div>
                      <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: '#232B3E' }}>
                        {selectedSession.ai_companion_insights.studentProgress}
                      </div>
                    </div>
                  )}
                  {selectedSession.ai_companion_insights.difficultyLevel && (
                    <div>
                      <div style={{ fontSize: isMobile ? 12 : 13, color: '#6B7280', marginBottom: 4 }}>
                        {isArabic ? 'مستوى الصعوبة' : 'Difficulty Level'}
                      </div>
                      <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: '#232B3E' }}>
                        {selectedSession.ai_companion_insights.difficultyLevel}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
} 